'use client';

import { useState } from 'react';
import { ChevronDown, ArrowUpRight } from 'lucide-react';

/* ==================================================================
   FAQ data — flat list (no category groupings)
   ================================================================== */

const faqs = [
  {
    q: 'So, what is the Offshore Software Development Service and how can Cloudastra help?',
    a: 'Offshore software development services include collaboration with the worldwide development team to construct, enhance, or sustain software solutions. Cloudastra assists companies to cut expenses, gain access to talented professionals and speed up their delivery schedules without settling with mediocrity. Our international offshore office is completely integrated into your workflows, providing such services as software product development (custom products), DevOps, AI integration and end-to-end project delivery. As a startup starting with MVP or an enterprise looking to extend its capabilities, our customized solutions work according to your needs and guarantee the best communication and results.',
  },
  {
    q: 'What are the benefits of your DevOps Managed Services to software delivery?',
    a: 'Our DevOps Managed Services accelerate software delivery by automating build, test and deployment pipelines, improving release frequency and reducing manual errors. We bring infrastructure-as-code, continuous integration, continuous deployment and proactive monitoring so your team can ship faster while staying reliable and secure.',
  },
  {
    q: 'Are you selling AI-based business-specific solutions?',
    a: 'Yes. We design and deliver AI solutions tailored to your business: large language model integrations, intelligent automation, computer vision pipelines, predictive analytics and agent-based workflows. Every solution is built around your data, your processes and your guardrails, not a generic template.',
  },
  {
    q: 'What are the industries your software development services traverse?',
    a: 'We partner with clients across finance, healthcare, retail, logistics, media, education and the public sector. Our delivery model adapts to each industry’s compliance, security and operational realities so the engineering choices fit the domain you operate in.',
  },
  {
    q: 'How is your Web App Development Services different to Web Development and Startups?',
    a: 'Our Web App Development goes beyond static sites: we build scalable, full-stack applications with secure authentication, database design, API integration and modern frontends. For startups we combine rapid MVP delivery with production-ready architecture, so you can move fast now without rebuilding later when traction comes in.',
  },
];

/* ==================================================================
   Accordion item — full-card style with light-blue active state
   ================================================================== */

function AccordionItem({ q, a, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-gray-200">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-6 py-4 text-left text-[15px] font-semibold leading-snug text-[#0a1b3a] transition hover:text-[#058BF0]"
      >
        <span>{q}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-gray-500 transition-transform duration-300 ${
            open ? 'rotate-180 text-[#058BF0]' : ''
          }`}
          strokeWidth={2.2}
        />
      </button>
      <div
        className="grid overflow-hidden text-sm leading-relaxed text-gray-600 transition-[grid-template-rows] duration-300 ease-out"
        style={{ gridTemplateRows: open ? '1fr' : '0fr' }}
      >
        <div className="min-h-0">
          <p className="pb-5 pr-8">{a}</p>
        </div>
      </div>
    </div>
  );
}

/* ==================================================================
   Section
   ================================================================== */

export default function FAQ() {
  return (
    <section
      data-nav-theme="light"
      className="relative bg-white py-20 lg:py-28"
    >
      <div className="mx-auto w-11/12 max-w-[1280px]">
        <div className="flex flex-col items-start gap-12 md:flex-row lg:gap-20">
          {/* Left column — heading + intro */}
          <div className="md:sticky md:top-32 md:w-5/12">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#058BF0] sm:text-sm">
              Frequently Asked
            </p>
            <h2 className="mt-5 font-poppins text-3xl font-bold leading-[1.2] sm:leading-[1.2] lg:leading-[1.2] tracking-tight text-[#0a1b3a] sm:text-4xl lg:text-5xl xl:text-[3.5rem]">
              Have questions?
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-gray-600 lg:text-lg">
              We&rsquo;re here to help you understand how we work. If you don&rsquo;t find your answer below, our team is one message away.
            </p>
            <a
              href="/contact"
              className="group mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#058BF0] underline-offset-4 hover:underline"
            >
              Reach out to our team
              <ArrowUpRight
                className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                strokeWidth={2.4}
              />
            </a>
          </div>

          {/* Right column — flat list of FAQ cards */}
          <div className="flex-1 space-y-3">
            {faqs.map((item, i) => (
              <AccordionItem
                key={i}
                q={item.q}
                a={item.a}
                defaultOpen={i === 0}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
